import {tokens} from '@commercial-helios/ui/theme'
import {colors, radii} from '@commercial-helios/ui/theme'
import React, {forwardRef} from 'react'
import {InView} from 'react-intersection-observer'
import styled, {css} from 'styled-components'

import {focusStyles} from '@helios/shared/styles/utils'

interface Props extends Omit<React.ComponentProps<typeof Slider>, 'as'> {
    onSlideChange?: (index: number) => void
}

export interface RefType {
    slideGoTo: (index: number) => void
}

const Carousel = forwardRef<RefType, React.PropsWithChildren<Props>>(
    ({children, onSlideChange, ...props}, ref) => {
        const slidesRefs = React.useRef<Array<HTMLElement | null>>([])

        React.useImperativeHandle(
            ref,
            () => ({
                slideGoTo(index: number) {
                    slidesRefs.current[index]?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'nearest',
                        inline: 'center',
                    })
                },
            }),
            []
        )

        const setActiveSlideCallback = React.useCallback(
            (index) => {
                onSlideChange && onSlideChange(index)
            },
            [onSlideChange]
        )

        return (
            <Slider {...props}>
                {React.Children.map(
                    children,
                    (child, index) =>
                        child && (
                            <InView
                                key={index}
                                threshold={0.5}
                                onChange={(inView) => inView && setActiveSlideCallback(index)}
                            >
                                <Slide ref={(el) => (slidesRefs.current[index] = el)} tabIndex={-1}>
                                    {child}
                                </Slide>
                            </InView>
                        )
                )}
            </Slider>
        )
    }
)

const Slider = styled.ul`
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: 100%;
    padding: 0;
    margin: 0;
    overflow-x: auto;
    list-style: none;
    scroll-behavior: smooth;
    scrollbar-width: none;
    scroll-snap-type: x mandatory;

    ::-webkit-scrollbar {
        display: none;
    }
`

const Slide = styled.div`
    height: 100%;
    scroll-snap-align: center;
    scroll-snap-stop: always;
`

interface BulletProps {
    $isActive?: boolean
}

export const Bullet = styled.button<BulletProps>(({$isActive}) => {
    const isActiveStyles = css`
        ::after {
            background: ${colors.background.tertiary};
            transform: scale(1.25);
        }
    `

    return css`
        padding: ${tokens.spacing8};
        cursor: pointer;
        background: transparent;
        border: none;
        -webkit-appearance: none;

        ::after {
            display: block;
            width: ${tokens.spacing16};
            height: ${tokens.spacing16};
            content: '';
            background: ${colors.background.secondary};
            border-radius: ${radii.circle};
            transition: transform ${tokens.fastInOut};
        }

        :focus {
            outline: none;

            ::after {
                ${focusStyles()}
            }
        }

        ${$isActive && isActiveStyles}
    `
})

export default Carousel
